import { FC } from 'react';
import styled, { css } from 'styled-components';

import { checkIfWindows } from '@/utils/helpers';

interface FillerProps {
  /** Font family to preload */
  fontFamily?: string;
  /** Font size (default: 45px) */
  fontSize?: string;
  /** Line height (default: 1.2) */
  lineHeight?: string;
  /** Color (default: #000) */
  color?: string;
  /** Font weight (default: 400) */
  fontWeight?: string | number;
  /** Letter spacing (optional) */
  letterSpacing?: string;
  /** Text shadow (optional) */
  textShadow?: string;
  /** Windows-specific text shadow (optional) */
  windowsTextShadow?: string;
  /** Additional custom styles */
  customStyles?: string;
}

const CustomText = styled.div<{
  $isWindows: boolean;
  $fontFamily?: string;
  $fontSize?: string;
  $lineHeight?: string;
  $color?: string;
  $fontWeight?: string | number;
  $letterSpacing?: string;
  $textShadow?: string;
  $windowsTextShadow?: string;
  $customStyles?: string;
}>`
  visibility: hidden;
  position: fixed;
  left: -10000px;
  top: -10000px;
  font-size: ${({ $fontSize }) => $fontSize || '45px'};
  line-height: ${({ $lineHeight }) => $lineHeight || '1.2'};
  text-align: center;
  color: ${({ $color }) => $color || '#000'};
  font-family: ${({ $fontFamily }) => $fontFamily || 'CustomArial'};
  font-weight: ${({ $fontWeight }) => $fontWeight || 400};
  ${({ $letterSpacing }) => $letterSpacing && `letter-spacing: ${$letterSpacing};`}
  ${({ $textShadow }) =>
    $textShadow &&
    css`
      -webkit-text-shadow: ${$textShadow};
      -moz-text-shadow: ${$textShadow};
      -ms-text-shadow: ${$textShadow};
      -o-text-shadow: ${$textShadow};
      text-shadow: ${$textShadow};
    `}
  ${({ $isWindows, $windowsTextShadow }) =>
    $isWindows &&
    $windowsTextShadow &&
    css`
      -webkit-text-shadow: ${$windowsTextShadow};
      -moz-text-shadow: ${$windowsTextShadow};
      -ms-text-shadow: ${$windowsTextShadow};
      -o-text-shadow: ${$windowsTextShadow};
      text-shadow: ${$windowsTextShadow};
    `}
  ${({ $customStyles }) => $customStyles}
`;

/**
 * Shared Filler component for preloading fonts in cover image variations
 * Renders an invisible text element to ensure fonts are loaded before image generation
 *
 * @example
 * // Basic usage with default Arial font
 * <Filler />
 *
 * @example
 * // With custom font and styling
 * <Filler
 *   fontFamily="Gothic"
 *   fontSize="50px"
 *   fontWeight={700}
 *   textShadow="1.5px 0 0 #000"
 *   windowsTextShadow="1.9px 0 0 #000"
 * />
 */
const Filler: FC<FillerProps> = ({
  fontFamily,
  fontSize,
  lineHeight,
  color,
  fontWeight,
  letterSpacing,
  textShadow,
  windowsTextShadow,
  customStyles,
}) => {
  return (
    <CustomText
      $isWindows={checkIfWindows()}
      $fontFamily={fontFamily}
      $fontSize={fontSize}
      $lineHeight={lineHeight}
      $color={color}
      $fontWeight={fontWeight}
      $letterSpacing={letterSpacing}
      $textShadow={textShadow}
      $windowsTextShadow={windowsTextShadow}
      $customStyles={customStyles}
    >
      Some random text
    </CustomText>
  );
};

export default Filler;
